import { useLayoutEffect, useRef } from 'react'
import { setLabel } from '@/lib/setLabel'
import { useNow } from '@/hooks/useNow'
import { useTimeFormat } from '@/hooks/useTimeFormat'
import { drawWallpaper, type WallpaperDrawing } from './drawWallpaper'

const PREVIEW_HEIGHT = 300

// The image shown on a mock lock screen, in the phone's own proportions: the
// same drawing the export makes, at preview size — so the preview is exactly
// what gets saved — with a stand-in clock where the real one will sit, so the
// empty top reads as room for the clock. The clock is laid over the canvas,
// never drawn into it. A visually hidden list names the sets for screen
// readers (and tests), since a canvas has no text to read.
export function WallpaperPreview({ drawing }: { drawing: WallpaperDrawing }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { frame } = drawing.layout
  const width = (frame.width * PREVIEW_HEIGHT) / frame.height
  const clock = useLockScreenClock()

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const pixelScale = (width * (window.devicePixelRatio || 1)) / frame.width
    canvas.width = Math.round(frame.width * pixelScale)
    canvas.height = Math.round(frame.height * pixelScale)
    drawWallpaper(ctx, drawing, pixelScale)
  }, [drawing, frame, width])

  return (
    <div
      className="relative mx-auto overflow-hidden rounded-[22px] ring-4 ring-muted"
      style={{ width, height: PREVIEW_HEIGHT }}
    >
      <canvas
        ref={canvasRef}
        data-testid="wallpaper-image"
        role="img"
        aria-label={`${drawing.text.eventName} · ${drawing.text.dateLabel}`}
        className="block size-full"
      />
      <div
        aria-hidden
        data-testid="wallpaper-preview-clock"
        className="pointer-events-none absolute inset-x-0 flex flex-col items-center text-on-image/90"
        style={{ top: PREVIEW_HEIGHT * 0.05 }}
      >
        <p className="font-semibold first-letter:uppercase" style={{ fontSize: width * 0.044 }}>
          {drawing.text.dateLabel}
        </p>
        <p className="font-bold leading-none tracking-tight tabular-nums" style={{ fontSize: Math.min(width * 0.23, PREVIEW_HEIGHT * 0.105) }}>{clock}</p>
      </div>
      <ul className="sr-only">
        {drawing.layout.empty && <li>{drawing.text.emptyLabel[drawing.layout.empty]}</li>}
        {drawing.layout.blocks.map(b => (
          <li key={b.set.id} data-testid="wallpaper-set" data-status={b.status ?? 'none'} data-dimmed={b.dimmed}>
            {setLabel(b.set, drawing.artists).name} · {drawing.text.clockRange(b.set.startTime, b.set.endTime)}
          </li>
        ))}
      </ul>
    </div>
  )
}

// The time now, the way a lock screen shows it: no AM/PM in 12-hour time.
function useLockScreenClock(): string {
  const now = useNow()
  const { hourCycle } = useTimeFormat()
  const h = now.getHours()
  const hours = hourCycle === '24h' ? h : h % 12 || 12
  return `${hours}:${String(now.getMinutes()).padStart(2, '0')}`
}
